import React, {useCallback, useMemo, useState} from "react";
import {gql, useQuery} from "@apollo/client";
import {ReferralOfferModal} from "./initial-offer-date-update/ReferralOfferModal";
import {ProviderReferralOfferTable} from "./ProviderReferralOfferTable";
import {showToast} from "common";
import {BehaviorSubject} from "rxjs";
import {AuthorizationInfo} from "../domain";
import {StaffingPlanFormModal} from "../staffing-plan/StaffingPlanFormModal";
import {ConsultWithCCMFormModal} from "../offer-at-risk/consult-with-ccm/ConsultWithCCMFormModal";
import {useAuthStaffingPlan} from "../client-profile-authorization/mutation/useAuthStaffingPlan";
import {useAuthConsultWithCCM} from "../client-profile-authorization/mutation/useAuthConsultWithCCM";
import {LoadingSpinner} from "../../../common";
import { ReferralOffer } from "./domain";

type Props = {
    clientGuid: string;
};

const REFERRAL_OFFERS_BY_CLIENT_QUERY = gql`
    query ProviderReferralOffersByClientGuid($clientGuid: String!) {
        providerReferralOffersByClientGuid(clientGuid: $clientGuid) {
            id
            guid
            status
            originalOfferGuid
            firstServiceDate
            submittedAt
            serviceLocationType
            serviceLocationParaprofessionalType
            serviceLocationMidLevelType
            serviceLocationHighLevelType
            serviceLocationGroupCareType
            provideServicesInClinic
            provideServiceInRequiredLanguage
            provideAllEstimatedHours
            directCareHours
            midLevelSupervisionHours
            highLevelSupervisionHours
            groupCareHours
            comments
            caregiverMediatedMidLevelHours
            caregiverMediatedHighLevelHours
            treatmentOffered
            startDateCaregiverMediatedTreatment
            startDatePractMediatedTreatment
            psychotherapyOffer
            lastServiceDate
            referral {
                id
                guid
                caseNumber
                serviceTypes
                serviceLocations
                status
                placementType
                matchedDate
                requiredLanguage
                directCareHours
                midLevelHours
                highLevelHours
                groupCareHours
                authDirectCareHours
                authDirectCareBeneficialHours
                authMidLevelHours
                authMidLevelBeneficialHours
                authHighLevelHours
                authHighLevelBeneficialHours
                authGroupCareHours
                authGroupCareBeneficialHours
                careMediatedMidHours
                careMediatedHighHours
                careMediatedMidMinHours
                careMediatedHighMinHours
                practDirectBeneficialHours
                practMidBeneficialHours
                practHighBeneficialHours
                practGroupBeneficialHours
                practDirectOptimalHours
                practMidOptimalHours
                practHighOptimalHours
                practGroupOptimalHours
            }
            submittedBy {
                firstName
                lastName
            }
            draftStaffingPlan {
                comments
                fullOfferStartDate
                reviewedAndApprovedByClientFamilyDate
                offeredParaprofessionalHours
                offeredMidLevelHours
                offeredHighLevelHours
                offeredGroupCareHours
            }
            authorization {
                guid
                status
                hasBilledHours
                numOfTier
                serviceType1
                serviceType2
                serviceType3
                serviceType4
                serviceType5
                serviceType6
                isDraft1
                isDraft2
                isDraft3
                isDraft4
                isDraft5
                isDraft6
                careMediatedMidMinHours
                careMediatedMidHours
                careMediatedHighMinHours
                careMediatedHighHours
                practDirectBeneficialHours
                practDirectOptimalHours
                practMidBeneficialHours
                practMidOptimalHours
                practHighBeneficialHours
                practHighOptimalHours
                practGroupBeneficialHours
                practGroupOptimalHours
            }
        }
    }
`;

// Module-level BehaviorSubjects for target modal visibility
const showStaffingPlan$ = new BehaviorSubject<boolean>(false);
const showConsultWithCCM$ = new BehaviorSubject<boolean>(false);

export function ClientProfileReferralOfferPage({clientGuid}: Props) {
    const [selectedOffer, setSelectedOffer] = useState<ReferralOffer | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedAuthorization, setSelectedAuthorization] = useState<AuthorizationInfo | null>(null);
    const selectOffer = useCallback((offer: ReferralOffer | null) => {
      if (offer !== null) {
        setSelectedAuthorization(offer?.authorization);
      }
      setSelectedOffer(offer);
    }, [setSelectedOffer, setSelectedAuthorization]);

    const {data, loading, error} = useQuery(REFERRAL_OFFERS_BY_CLIENT_QUERY, {
        variables: {clientGuid},
    });

    const offers: ReferralOffer[] = useMemo(() => {
        return data?.providerReferralOffersByClientGuid ?? [];
    }, [data]);

    const authGuid = selectedAuthorization?.guid ?? "";
    const {submitStaffingPlan} = useAuthStaffingPlan(authGuid);
    const {submitConsultWithCCM} = useAuthConsultWithCCM(authGuid);

    const handleOpenModal = (offer: ReferralOffer) => {
        selectOffer(offer);
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setTimeout(() => selectOffer(null), 500);
    };

  const handleRequireStaffingPlan = (_offer: ReferralOffer) => {
        showToast.closeAll();
        setModalVisible(false);
        showStaffingPlan$.next(true);
    };

  const handleRequireClinicalConsult = (_offer: ReferralOffer) => {
        showToast.closeAll();
        setModalVisible(false);
        showConsultWithCCM$.next(true);
    };

    if (loading && !data) {
      return <LoadingSpinner spacing={20} height={"40vh"}/>;
    }
    if (error) {
      return <div className="p-4 text-red-600">Error loading referral offers: {error.message}</div>;
    }

    return (<>
      <ProviderReferralOfferTable offers={offers} onSelectOffer={handleOpenModal}/>

      <ReferralOfferModal
          key={selectedOffer?.guid}
          clientGuid={clientGuid}
          visible={modalVisible}
          offer={selectedOffer}
          onClose={handleCloseModal}
          onRequireStaffingPlan={handleRequireStaffingPlan}
          onRequireClinicalConsult={handleRequireClinicalConsult}
      />

      {selectedAuthorization && (
          <>
              <StaffingPlanFormModal
                  visible$={showStaffingPlan$}
                  authorization={selectedAuthorization}
                  onFinished={submitStaffingPlan}
                  isAuth
              />

              <ConsultWithCCMFormModal
                  visible$={showConsultWithCCM$}
                  onFinished={submitConsultWithCCM}
              />
          </>
      )}
    </>);
}
